import { useEffect, useRef } from 'react';

import type { GeographyMode } from '../../../../shared/user-preferences/types';
import { useToast } from '../../hooks/useToast';
import {
  geographyAdministrativeBranchLabels,
  geographyBranchLabels,
} from '../constants/branch-labels';
import { generatePageKey } from '../engine/filterGroupConsistency';
import type { FilterAction } from '../engine/filterReducer';
import {
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type MultiFilter,
  type PrimarySelection,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';
import { getActiveSelections } from '../utils/primaryFilterUtils';

// Hardcoded default primary selections for when all primary filters are
// incompatible with the new geography mode and need to be replaced.
// Tree IDs follow the pattern: regionId_countryId_entityId
const DEFAULT_STATE_PRIMARY_SELECTIONS: PrimarySelection[] = [
  { id: '1_1_2240', name: 'New York', levelId: 'state', isActive: true },
  { id: '5_4_984', name: 'England', levelId: 'state', isActive: true },
  { id: '3_8_1354', name: 'Ile-De-France', levelId: 'state', isActive: true },
  { id: '1_1_572', name: 'California', levelId: 'state', isActive: true },
  { id: '4_5_2997', name: 'Sao Paulo', levelId: 'state', isActive: true },
];

const DEFAULT_MSA_PRIMARY_SELECTIONS: PrimarySelection[] = [
  { id: '5_4_1', name: 'London', levelId: 'msa', isActive: true },
  { id: '3_8_5', name: 'Paris', levelId: 'msa', isActive: true },
  { id: '1_1_2', name: 'New York City', levelId: 'msa', isActive: true },
  { id: '1_1_7', name: 'San Francisco', levelId: 'msa', isActive: true },
  { id: '4_5_47', name: 'Sao Paulo', levelId: 'msa', isActive: true },
];

const GEOGRAPHY_SELECTION_LIST_IDS: Record<
  GeographyMode,
  TreeFilter['selectionListIds']
> = {
  administrative: ['region', 'country', 'state', 'city'],
  msa: ['region', 'country', 'msa', 'city'],
};

/**
 * Returns the selection list IDs for a given geography mode.
 */
export const getGeographySelectionListIds = (
  mode: GeographyMode
): TreeFilter['selectionListIds'] => GEOGRAPHY_SELECTION_LIST_IDS[mode];

const levelIdFromSelectionId = (
  selectionId: string,
  selectionListIds: TreeFilter['selectionListIds']
): TreeSelection['levelId'] | undefined => {
  const segments = String(selectionId).split('_');
  if (segments.length <= 1) {
    return undefined;
  }
  const depth = segments.length - 1;
  return selectionListIds[depth];
};

const recalcGeographyTreeLevelIds = (
  treeFilter: TreeFilter,
  selectionListIds: TreeFilter['selectionListIds']
): TreeFilter => {
  const mapSelection = (sel: TreeSelection): TreeSelection => {
    const levelId = levelIdFromSelectionId(sel.id, selectionListIds);
    if (!levelId) return sel;
    return { ...sel, levelId };
  };

  const selections = (treeFilter.selections ?? []).map(mapSelection);
  const primarySelections = treeFilter.primarySelections?.map((sel) => {
    const levelId = levelIdFromSelectionId(sel.id, selectionListIds);
    if (!levelId) return sel;
    return { ...sel, levelId };
  });

  return {
    ...treeFilter,
    selectionListIds,
    selections,
    ...(primarySelections ? { primarySelections } : {}),
  };
};

/**
 * Applies geography mode (selection list IDs + branch labels + levelId derived
 * from id depth) to a single geography tree filter.
 */
const applyGeographyModeToTreeFilter = (
  treeFilter: TreeFilter,
  geographyMode: GeographyMode
): TreeFilter => {
  const selectionListIds = getGeographySelectionListIds(geographyMode);
  const branches =
    geographyMode === 'administrative'
      ? geographyAdministrativeBranchLabels
      : geographyBranchLabels;

  const withLevelIds = recalcGeographyTreeLevelIds(
    treeFilter,
    selectionListIds
  );

  return {
    ...withLevelIds,
    branches,
  };
};

/**
 * Adapts the active filter map so the geography filter reflects the current
 * geography mode (selection list IDs + branch labels). Does NOT strip
 * incompatible selections — that is handled by `useGeographyModeChangeEffect`.
 */
export const applyGeographyModeToFilterMap = (
  filterMap: FilterSelectionsMap,
  geographyMode: GeographyMode
): FilterSelectionsMap => {
  const map = new Map(filterMap);

  const geoFilter = map.get(FilterId.GEOGRAPHY) as TreeFilter | undefined;
  if (geoFilter) {
    map.set(
      FilterId.GEOGRAPHY,
      applyGeographyModeToTreeFilter(geoFilter, geographyMode)
    );
  }

  const multiGeo = map.get(FilterId.MULTI_GEOGRAPHY) as
    | MultiFilter<TreeFilter>
    | undefined;
  if (
    multiGeo &&
    multiGeo.type === FilterType.MULTI &&
    Array.isArray(multiGeo.instances)
  ) {
    const instances = multiGeo.instances.map((inst) =>
      inst.type === FilterType.TREE
        ? applyGeographyModeToTreeFilter(inst, geographyMode)
        : inst
    );
    map.set(FilterId.MULTI_GEOGRAPHY, { ...multiGeo, instances });
  }

  return map;
};

const stripIncompatibleSelectionsFromGeographyTree = (
  treeFilter: TreeFilter,
  geographyMode: GeographyMode
): TreeFilter | null => {
  const incompatibleLevelId =
    geographyMode === 'administrative' ? 'msa' : 'state';

  const selections = treeFilter.selections ?? [];
  const primarySelections = treeFilter.primarySelections ?? [];

  const hasIncompatibleSelections = selections.some(
    (s) => s.levelId === incompatibleLevelId
  );
  const hasIncompatiblePrimary = primarySelections.some(
    (s) => s.levelId === incompatibleLevelId
  );

  if (!hasIncompatibleSelections && !hasIncompatiblePrimary) {
    return null;
  }

  const strippedSelections = selections.filter(
    (s) => s.levelId !== incompatibleLevelId
  );
  const strippedPrimary = primarySelections.filter(
    (s) => s.levelId !== incompatibleLevelId
  );

  let finalPrimary: PrimarySelection[] | undefined;
  if (strippedPrimary.length > 0) {
    const hasAnyActive = strippedPrimary.some((s) => s.isActive);
    finalPrimary = hasAnyActive
      ? strippedPrimary
      : strippedPrimary.map((s, idx) => ({
          ...s,
          isActive: idx === 0,
        }));
  } else if (hasIncompatiblePrimary) {
    finalPrimary =
      geographyMode === 'administrative'
        ? DEFAULT_STATE_PRIMARY_SELECTIONS
        : DEFAULT_MSA_PRIMARY_SELECTIONS;
  }

  return {
    ...treeFilter,
    selections: finalPrimary
      ? getActiveSelections(finalPrimary)
      : strippedSelections,
    primarySelections: finalPrimary,
  };
};

/**
 * When geography mode changes, strips incompatible selections from the
 * geography filter, dispatches a REPLACE_FILTERS action to persist changes,
 * and shows a toast notification.
 */
export const useGeographyModeChangeEffect = ({
  geographyMode,
  normalizedActiveFilterMap,
  filterStateActiveKey,
  revelioPageId,
  primaryFilterSelectionDefault,
  pageId,
  dispatch,
}: {
  geographyMode: GeographyMode;
  normalizedActiveFilterMap: FilterSelectionsMap;
  filterStateActiveKey: string | undefined;
  revelioPageId: string | undefined;
  primaryFilterSelectionDefault: number;
  pageId: string;
  dispatch: React.Dispatch<FilterAction>;
}) => {
  const toast = useToast();
  const prevGeographyModeRef = useRef(geographyMode);

  // Store the filter map in a ref so the effect always reads the latest
  // snapshot without needing it in the dependency array (which would fire
  // on every filter change, not just geography mode changes).
  const filterMapRef = useRef(normalizedActiveFilterMap);
  filterMapRef.current = normalizedActiveFilterMap;

  // biome-ignore lint/correctness/useExhaustiveDependencies: only fire on geographyMode changes; other deps are stable or read via refs.
  useEffect(() => {
    if (prevGeographyModeRef.current === geographyMode) return;
    prevGeographyModeRef.current = geographyMode;

    const updatedFilterMap = new Map(filterMapRef.current);
    let didStrip = false;

    const geoFilter = updatedFilterMap.get(FilterId.GEOGRAPHY) as
      | TreeFilter
      | undefined;
    if (geoFilter) {
      const stripped = stripIncompatibleSelectionsFromGeographyTree(
        geoFilter,
        geographyMode
      );
      if (stripped) {
        updatedFilterMap.set(FilterId.GEOGRAPHY, stripped);
        didStrip = true;
      }
    }

    const multiGeo = updatedFilterMap.get(FilterId.MULTI_GEOGRAPHY) as
      | MultiFilter<TreeFilter>
      | undefined;
    if (
      multiGeo &&
      multiGeo.type === FilterType.MULTI &&
      Array.isArray(multiGeo.instances)
    ) {
      let anyInstanceStripped = false;
      const newInstances = multiGeo.instances.map((inst) => {
        if (inst.type !== FilterType.TREE) return inst;
        const stripped = stripIncompatibleSelectionsFromGeographyTree(
          inst,
          geographyMode
        );
        if (stripped) {
          anyInstanceStripped = true;
          return stripped;
        }
        return inst;
      });
      if (anyInstanceStripped) {
        updatedFilterMap.set(FilterId.MULTI_GEOGRAPHY, {
          ...multiGeo,
          instances: newInstances,
        });
        didStrip = true;
      }
    }

    if (!didStrip) return;

    const activeKey =
      filterStateActiveKey ??
      geoFilter?.id ??
      multiGeo?.id ??
      FilterId.GEOGRAPHY;
    const pageKey = generatePageKey(activeKey, revelioPageId ?? pageId);

    dispatch({
      type: 'REPLACE_FILTERS',
      payload: {
        filterMap: updatedFilterMap,
        pageKey,
        defaultActiveCount: primaryFilterSelectionDefault,
        skipReconciliation: true,
      },
    });

    const removedLabel =
      geographyMode === 'administrative' ? 'Metro Area' : 'State';
    toast({
      title: `${removedLabel} filters removed`,
      description: `State and Metro Area filters cannot be active at the same time.`,
      status: 'info',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    });
  }, [geographyMode]);
};
